import * as React from 'react'
import { useSelector } from 'react-redux'
import { getActiveAccountSlug } from 'auth/selectors'
import { GlobalSearchQueryError, useSearchQuery } from '../reduxQueries'
import { GroupResult, MIN_QUERY_LENGTH, SEARCH_TYPE_META, SearchResult, SearchTypeMeta } from '../schema'
import { useDebouncedValue } from './useDebouncedValue'

const DEBOUNCE_MS = 300
const GROUP_LIMIT = 5

// A full email address that returned nothing often means the STORED address
// holds the typo (guest never got their tickets, contacts support with the
// correct one). Retrying with just the local part bridges any domain typo.
const EMAIL_QUERY_PATTERN = /^\S+@\S+\.\S+$/

export interface OrderedGroup {
    meta: SearchTypeMeta
    group: GroupResult
}

export interface FlatRow {
    result: SearchResult
}

interface UseGlobalSearchParams {
    query: string
    enabled: boolean
}

// Debounced federated search over RTK Query, with the response shaped for
// rendering: groups ordered by total then top score (BUIL-541) and a flat row
// list driving keyboard navigation.
export const useGlobalSearch = ({ query, enabled }: UseGlobalSearchParams) => {
    const accountSlug = useSelector(getActiveAccountSlug)
    const trimmedQuery = query.trim()
    const debouncedQuery = useDebouncedValue(trimmedQuery, DEBOUNCE_MS)
    const hasQuery = trimmedQuery.length >= MIN_QUERY_LENGTH
    const skip = !enabled || !accountSlug || debouncedQuery.length < MIN_QUERY_LENGTH

    const { data, error, isFetching, refetch } = useSearchQuery(
        {
            accountSlug: accountSlug as string,
            q: debouncedQuery,
            limit: GROUP_LIMIT,
        },
        { skip },
    )

    const searchError = skip ? undefined : (error as GlobalSearchQueryError | undefined)
    const primaryGroups = skip || searchError ? undefined : data?.groups
    const primaryTotal = React.useMemo(
        () =>
            primaryGroups
                ? Object.values(primaryGroups).reduce((sum, group) => sum + (group?.results.length ?? 0), 0)
                : 0,
        [primaryGroups],
    )

    // Local-part fallback for full-email queries with zero hits.
    const emailLocalPart = React.useMemo(() => {
        if (!EMAIL_QUERY_PATTERN.test(debouncedQuery)) {
            return ''
        }
        const localPart = debouncedQuery.split('@')[0]
        return localPart.length >= MIN_QUERY_LENGTH ? localPart : ''
    }, [debouncedQuery])
    const fallbackSkip = skip || !!searchError || !data || isFetching || primaryTotal > 0 || !emailLocalPart
    const fallback = useSearchQuery(
        {
            accountSlug: accountSlug as string,
            q: emailLocalPart,
            limit: GROUP_LIMIT,
        },
        { skip: fallbackSkip },
    )
    const fallbackGroups = fallbackSkip || fallback.error ? undefined : fallback.data?.groups
    const usingFallback =
        !fallbackSkip && !!fallbackGroups && Object.values(fallbackGroups).some((group) => group?.results.length)

    // Every automatic retry for an email-shaped query has come up empty — the
    // stored address may hold a typo we cannot bridge (e.g. in the local
    // part). The empty state should steer the operator to other identifiers.
    const primaryEmpty = !skip && !searchError && !!data && primaryTotal === 0
    const fallbackFinished = !fallbackSkip && !fallback.isFetching && (!!fallback.data || !!fallback.error)
    const emailSearchExhausted =
        primaryEmpty &&
        EMAIL_QUERY_PATTERN.test(debouncedQuery) &&
        (!emailLocalPart || (fallbackFinished && !usingFallback))

    const groups = usingFallback ? fallbackGroups : primaryGroups

    const orderedGroups: OrderedGroup[] = React.useMemo(
        () =>
            SEARCH_TYPE_META.map((meta) => ({ meta, group: groups?.[meta.key] }))
                .filter((entry): entry is OrderedGroup => Boolean(entry.group && entry.group.results.length))
                .sort(
                    (a, b) =>
                        b.group.total - a.group.total ||
                        (b.group.results[0]?.score ?? 0) - (a.group.results[0]?.score ?? 0),
                ),
        [groups],
    )

    const flatRows: FlatRow[] = React.useMemo(
        () => orderedGroups.flatMap(({ group }) => group.results.map((result) => ({ result }))),
        [orderedGroups],
    )

    return {
        accountSlug,
        hasQuery,
        // The query the current results belong to (session tracking keys steps on it).
        debouncedQuery,
        errorCode: searchError?.code ?? null,
        // Loading covers the debounce window, the request and the fallback.
        loading: hasQuery && (isFetching || fallback.isFetching || trimmedQuery !== debouncedQuery),
        // Just the network part of `loading` (session tracking times requests with it).
        fetching: isFetching || fallback.isFetching,
        // Set when results come from the local-part retry of an email query.
        emailFallback: usingFallback ? { query: debouncedQuery, localPart: emailLocalPart } : null,
        emailSearchExhausted,
        orderedGroups,
        flatRows,
        filtersDropped: !searchError && !!data?.filterErrors && Object.keys(data.filterErrors).length > 0,
        errorMessage: searchError
            ? searchError.code !== 'unknown'
                ? searchError.message
                : 'Search is unavailable right now. Please try again.'
            : null,
        retry: refetch,
    }
}
